// Shared by club pages, article downloads, and newsletter forms.
window.JPClubSignup = window.JPClubSignup || (() => {
  function errorName(error) {
    return typeof error?.name === "string" && /^[A-Za-z][A-Za-z0-9]{0,39}$/.test(error.name)
      ? error.name : "Error";
  }

  function redact(value, limit) {
    return (typeof value === "string" ? value : "")
      .replace(/https?:\/\/[^\s"'<>]+/gi, "[url]")
      .replace(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi, "[email]")
      .slice(0, limit);
  }

  async function responseCode(response) {
    try {
      const code = (await response.json())?.errors?.[0]?.code;
      return typeof code === "string" && /^[A-Za-z][A-Za-z0-9._-]{0,63}$/.test(code)
        ? code : null;
    } catch {
      return null;
    }
  }

  async function subscribe(email, listId, config) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);
    try {
      const response = await fetch(
        "https://a.klaviyo.com/client/subscriptions?company_id=" + encodeURIComponent(config.companyId),
        {
          method: "POST",
          signal: controller.signal,
          headers: { revision: config.revision, "content-type": config.contentType },
          body: JSON.stringify({
            data: {
              type: "subscription",
              attributes: {
                custom_source: config.customSource,
                profile: { data: { type: "profile", attributes: { email } } }
              },
              relationships: { list: { data: { type: "list", id: listId } } }
            }
          })
        }
      );
      if (!response.ok) {
        const error = new Error("Klaviyo subscription failed with status " + response.status);
        error.status = response.status;
        error.responseCode = await responseCode(response);
        throw error;
      }
      return response.status;
    } catch (error) {
      const failure = new Error("Klaviyo subscription request failed");
      failure.status = error?.status ?? null;
      failure.errorType = failure.status !== null ? "http" : controller.signal.aborted ? "timeout" : "network";
      failure.errorName = errorName(error);
      failure.responseCode = error?.responseCode ?? null;
      failure.diagnosticMessage = redact(error?.message, 200);
      failure.diagnosticStack = redact(error?.stack, 1000);
      throw failure;
    } finally {
      clearTimeout(timeout);
    }
  }

  function selectedLists(form, primaryListId) {
    const optional = Array.from(form.elements)
      .filter((field) => field.matches?.('input[type="checkbox"][data-klaviyo-list-id]:checked'))
      .map((field) => field.dataset.klaviyoListId);
    return [...new Set([primaryListId, ...optional].filter(Boolean))];
  }

  function clarityAllowed(config) {
    return typeof window.clarity === "function" &&
      (!config.requireConsent || Boolean(window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.()));
  }

  function reportClarity(email, form, detail, config) {
    try {
      if (!clarityAllowed(config)) return;
      Promise.resolve(window.clarity("identify", String(email).trim().toLowerCase())).catch(() => null);
      window.clarity("set", "klaviyo_club_form", form.id || form.className || "unknown");
      window.clarity("set", "klaviyo_club_source", config.formSource);
      window.clarity("set", "klaviyo_club_outcome", detail.outcome);
      window.clarity("set", "klaviyo_club_primary_succeeded", String(detail.primarySucceeded));
      window.clarity("set", "klaviyo_club_failed_list", detail.failedListIds);
      window.clarity("set", "klaviyo_club_failure_code", detail.failures.map((failure) =>
        failure.list_id + "_" + failure.error_type + "_" + (failure.status ?? "unknown")));
      window.clarity("set", "klaviyo_club_error_name", detail.clarityFailures.map((failure) =>
        failure.list_id + "_" + failure.error_name));
      const codes = detail.clarityFailures.filter((failure) => failure.response_code)
        .map((failure) => failure.list_id + "_" + failure.response_code);
      if (codes.length) window.clarity("set", "klaviyo_club_response_code", codes);
      window.clarity("event", "klaviyo_club_form_" + detail.outcome);
      window.clarity("upgrade", "klaviyo_club_form_failure");
    } catch {
      // Analytics must never interrupt a signup.
    }
  }

  function reportTelemetryError(error, controller, config) {
    const status = error?.status ?? null;
    const type = status !== null ? "http" : controller.signal.aborted ? "timeout" : "network";
    console.warn("Unable to report Klaviyo club form failure", {
      status, errorType: type, errorName: errorName(error), responseCode: error?.responseCode ?? null,
      message: redact(error?.message, 200), stack: redact(error?.stack, 1000)
    });
    try {
      if (!clarityAllowed(config)) return;
      window.clarity("set", "klaviyo_club_telemetry_failure", type + "_" + (status ?? "unknown"));
      window.clarity("set", "klaviyo_club_telemetry_error_name", errorName(error));
      if (error?.responseCode) window.clarity("set", "klaviyo_club_telemetry_response_code", error.responseCode);
      window.clarity("event", "klaviyo_club_telemetry_error");
    } catch {
      // Analytics must never interrupt a signup.
    }
  }

  async function reportFailureEvent(email, form, detail, config) {
    if (config.requireConsent && !window.Shopify?.customerPrivacy?.marketingAllowed?.()) return;
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 10000);
    try {
      const response = await fetch(
        "https://a.klaviyo.com/client/events?company_id=" + encodeURIComponent(config.companyId),
        {
          method: "POST", signal: controller.signal, keepalive: true,
          headers: { revision: config.revision, "content-type": config.contentType },
          body: JSON.stringify({
            data: {
              type: "event",
              attributes: {
                unique_id: window.crypto?.randomUUID?.() || Date.now() + "-" + Math.random().toString(36).slice(2),
                metric: { data: { type: "metric", attributes: { name: "Klaviyo Club Form Submission Failed" } } },
                profile: { data: { type: "profile", attributes: { email } } },
                properties: {
                  outcome: detail.outcome, primary_succeeded: detail.primarySucceeded,
                  form_id: form.id || form.className || "unknown", form_source: config.formSource,
                  page_url: window.location.origin + window.location.pathname,
                  selected_list_ids: detail.selectedListIds,
                  successful_list_ids: detail.successfulListIds,
                  failed_list_ids: detail.failedListIds, failures: detail.failures
                }
              }
            }
          })
        }
      );
      if (!response.ok) {
        const error = new Error("Klaviyo failure event failed with status " + response.status);
        error.status = response.status;
        error.responseCode = await responseCode(response);
        throw error;
      }
    } catch (error) {
      reportTelemetryError(error, controller, config);
    } finally {
      clearTimeout(timeout);
    }
  }

  async function submit(form, email, config) {
    const listIds = selectedLists(form, config.primaryListId);
    if (!listIds.length || !config.companyId) throw new Error("Klaviyo form configuration is incomplete");
    const results = await Promise.allSettled(listIds.map((listId) => subscribe(email, listId, config)));
    const successfulListIds = listIds.filter((_, index) => results[index].status === "fulfilled");
    const clarityFailures = [];
    const diagnostics = [];
    const failures = results.flatMap((result, index) => {
      if (result.status === "fulfilled") return [];
      const reason = result.reason;
      clarityFailures.push({
        list_id: listIds[index], error_name: reason.errorName, response_code: reason.responseCode
      });
      diagnostics.push({
        list_id: listIds[index], error_name: reason.errorName, response_code: reason.responseCode,
        message: reason.diagnosticMessage, stack: reason.diagnosticStack
      });
      return [{ list_id: listIds[index], status: reason.status ?? null, error_type: reason.errorType }];
    });
    const detail = {
      email, selectedListIds: listIds, successfulListIds,
      failedListIds: failures.map((failure) => failure.list_id), failures, clarityFailures,
      primarySucceeded: successfulListIds.includes(config.primaryListId),
      outcome: failures.length === 0 ? "success" : successfulListIds.length ? "partial" : "error"
    };
    if (failures.length) {
      console.error("Klaviyo club form subscription failed", {
        formId: form.id || form.className, failures, diagnostics
      });
      reportClarity(email, form, detail, config);
      void reportFailureEvent(email, form, detail, config);
      for (const field of Array.from(form.elements)) {
        if (field.matches?.('input[type="checkbox"][data-klaviyo-list-id]:checked') &&
            successfulListIds.includes(field.dataset.klaviyoListId)) field.checked = false;
      }
    }
    form.dispatchEvent(new CustomEvent("klaviyo:club-form:" + detail.outcome, { bubbles: true, detail }));
    return detail;
  }

  function message(detail, email, successMessage, errorMessage) {
    if (detail.outcome === "success") return successMessage || email + " subscribed to the newsletter";
    if (detail.outcome === "partial" && detail.primarySucceeded) {
      return "The main club signup succeeded, but some optional clubs failed. Please retry the selected options.";
    }
    if (detail.outcome === "partial") {
      return "Some optional clubs succeeded, but the main club signup failed. Please try again.";
    }
    return errorMessage || "An error occurred, please try again";
  }

  return { submit, message };
})();
